import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';

import Landing from './pages/Landing';
import { LoginPage, SignupPage, ForgotPasswordPage, ForgotUsernamePage } from './pages/AuthPages';
import Dashboard from './pages/Dashboard';
import { PrivacyPolicyPage, TermsOfServicePage } from './pages/Legal';

function AppContent() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // Safety net: never leave the user stuck on the loading screen.
    const safety = setTimeout(() => { if (!cancelled) setLoading(false); }, 5000);

    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const { data: profile } = await supabase
            .from('user_profiles').select('*').eq('user_id', session.user.id).single();
          if (profile && !cancelled) {
            setCurrentUser({ ...session.user, ...profile });
            setIsAuthenticated(true);
          }
        }
      } catch (err) {
        console.error('Auth init failed:', err);
      } finally {
        clearTimeout(safety);
        if (!cancelled) setLoading(false);
      }
    };
    initAuth();

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      // Defer Supabase calls out of this callback: awaiting them inside it
      // deadlocks supabase-js (the auth lock is held while the callback runs),
      // which can hang getSession() and freeze the app on the loading screen.
      setTimeout(async () => {
        if (event === 'SIGNED_IN' && session?.user) {
          const { data: profile } = await supabase
            .from('user_profiles').select('*').eq('user_id', session.user.id).single();
          if (profile) {
            setCurrentUser({ ...session.user, ...profile });
            setIsAuthenticated(true);
          }
        } else if (event === 'SIGNED_OUT') {
          setCurrentUser(null);
          setIsAuthenticated(false);
        }
      }, 0);
    });

    return () => {
      cancelled = true;
      clearTimeout(safety);
      authListener.subscription.unsubscribe();
    };
  }, []);

  const handleLogin = async (userLike) => {
    // Accepts either a prepared user object (from Login/Signup pages)
    // or email/password credentials.
    if (userLike?.email && userLike?.password) {
      const { data, error } = await supabase.auth.signInWithPassword(userLike);
      if (!error && data.user) {
        const { data: profile } = await supabase
          .from('user_profiles').select('*').eq('user_id', data.user.id).single();
        if (profile) {
          setCurrentUser({ ...data.user, ...profile });
          setIsAuthenticated(true);
          navigate('/dashboard');
        }
      }
      return { data, error };
    }
    setCurrentUser(userLike);
    setIsAuthenticated(true);
    navigate('/dashboard');
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setCurrentUser(null);
    setIsAuthenticated(false);
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '1rem' }}>
        <img src="/battery-icon.svg" alt="" style={{ width: 48, height: 48, animation: 'spin 1.6s linear infinite' }} />
        <span style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>Loading Lithix-cell…</span>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Landing navigate={navigate} />} />
      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/dashboard" /> : <LoginPage navigate={navigate} handleLogin={handleLogin} />
      } />
      <Route path="/signup" element={
        isAuthenticated ? <Navigate to="/dashboard" /> : <SignupPage navigate={navigate} handleLogin={handleLogin} />
      } />
      <Route path="/privacy" element={<PrivacyPolicyPage navigate={navigate} />} />
      <Route path="/terms" element={<TermsOfServicePage navigate={navigate} />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage navigate={navigate} />} />
      <Route path="/forgot-username" element={<ForgotUsernamePage navigate={navigate} />} />
      <Route path="/dashboard" element={
        isAuthenticated
          ? <Dashboard currentUser={currentUser} navigate={navigate} handleLogout={handleLogout} setCurrentUser={setCurrentUser} />
          : <Navigate to="/login" />
      } />
      <Route path="/account" element={
        isAuthenticated
          ? <Dashboard currentUser={currentUser} navigate={navigate} handleLogout={handleLogout} setCurrentUser={setCurrentUser} initialSection="account" />
          : <Navigate to="/login" />
      } />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default function EVBatteryApp() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}
